import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";
import CircularProgress from "@mui/material/CircularProgress";
import { obtenerListaClientes } from "../../../store/clientes/thunkClientes";
import { ModalCliente } from "./ModalCliente";
import { UnificarModales } from "../Modales/UnificarModales";
import "./ListaClientes.css";

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: "48px",
        borderRadius: "14px",
        borderColor: state.isFocused ? "#0f766e" : "#d7dee7",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(15, 118, 110, 0.12)" : "none",
        backgroundColor: "#ffffff",
        "&:hover": {
            borderColor: "#0f766e",
        },
    }),
    placeholder: (base) => ({
        ...base,
        color: "#6b7280",
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: "#e6fffb",
        borderRadius: "999px",
        paddingInline: "4px",
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: "#115e59",
        fontWeight: 600,
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: "#115e59",
        borderRadius: "999px",
        ":hover": {
            backgroundColor: "#99f6e4",
            color: "#134e4a",
        },
    }),
    menu: (base) => ({
        ...base,
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: "0 18px 45px rgba(15, 23, 42, 0.14)",
    }),
};

const truncarTexto = (texto = "", limite = 32) => (texto.length > limite ? `${texto.substring(0, limite - 3)}...` : texto);

export const ListaClientes = () => {
    const dispatch = useDispatch();
    const [clientes, setClientes] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [clientsPerPage, setClientsPerPage] = useState(20);
    const [loading, setLoading] = useState(true);
    const [selectedClient, setSelectedClient] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [nombreCliente, setNombreCliente] = useState("");
    const [nombreEmpresa, setNombreEmpresa] = useState("");
    const [correoCliente, setCorreoCliente] = useState("");
    const [telefonoCliente, setTelefonoCliente] = useState("");
    const [tipoCliente, setTipoCliente] = useState([]);
    const [nameAdmin, setNameAdmin] = useState([]);

    const tipoClienteOptions = useMemo(() => {
        const tiposUnicos = [...new Set(clientes.map((cliente) => cliente.tipo_cliente_cliente))].filter(Boolean);
        return tiposUnicos
            .sort((a, b) => `${a}`.localeCompare(`${b}`, "es", { sensitivity: "base" }))
            .map((tipo) => ({ value: tipo, label: tipo }));
    }, [clientes]);

    const nameAdminOptions = useMemo(() => {
        const adminsUnicos = [...new Set(clientes.map((cliente) => cliente.name_admin))].filter(Boolean);
        return adminsUnicos
            .sort((a, b) => `${a}`.localeCompare(`${b}`, "es", { sensitivity: "base" }))
            .map((admin) => ({ value: admin, label: admin }));
    }, [clientes]);

    useEffect(() => {
        const fetchClientes = async () => {
            if (clientes.length > 0) {
                return;
            }

            setLoading(true);
            const response = await dispatch(obtenerListaClientes());
            setClientes(response || []);
            setLoading(false);
        };

        fetchClientes();
    }, [clientes.length, dispatch]);

    const filteredClientes = useMemo(
        () =>
            clientes.filter((cliente) => {
                const nombreMatch = nombreCliente === "" || cliente.nombre_cliente?.toLowerCase().includes(nombreCliente.toLowerCase());
                const empresaMatch = nombreEmpresa === "" || cliente.nombre_empresa?.toLowerCase().includes(nombreEmpresa.toLowerCase());
                const correoMatch = correoCliente === "" || cliente.correo_cleinte?.toLowerCase().includes(correoCliente.toLowerCase());
                const telefonoMatch = telefonoCliente === "" || cliente.telefono_cliente?.includes(telefonoCliente);
                const tipoMatch = tipoCliente.length === 0 || tipoCliente.some((tipo) => tipo.value === cliente.tipo_cliente_cliente);
                const adminMatch = nameAdmin.length === 0 || nameAdmin.some((admin) => admin.value === cliente.name_admin);

                return nombreMatch && empresaMatch && correoMatch && telefonoMatch && tipoMatch && adminMatch;
            }),
        [clientes, correoCliente, nameAdmin, nombreCliente, nombreEmpresa, telefonoCliente, tipoCliente],
    );

    const totalPages = Math.max(1, Math.ceil(filteredClientes.length / clientsPerPage));

    useEffect(() => {
        setCurrentPage(1);
    }, [clientsPerPage, filteredClientes.length]);

    const currentClients = useMemo(
        () => filteredClientes.slice((currentPage - 1) * clientsPerPage, currentPage * clientsPerPage),
        [clientsPerPage, currentPage, filteredClientes],
    );

    const handleClientsPerPageChange = (e) => {
        setClientsPerPage(parseInt(e.target.value, 10));
    };

    const handleRowClick = (cliente) => {
        setSelectedClient(cliente);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedClient(null);
    };

    if (loading) {
        return (
            <div className="clientes-list-loading">
                <CircularProgress />
            </div>
        );
    }

    return (
        <>
            <div className="clientes-list-view">
                <section className="clientes-list-shell">
                    <div className="clientes-list-hero">
                        <div>
                            <span className="clientes-list-eyebrow">Catalogo de clientes</span>
                            <h1 className="clientes-list-title">Clientes y cuentas empresariales</h1>
                            <p className="clientes-list-subtitle">
                                Consulta clientes, filtra por empresa, ejecutivo o tipo y abre cualquier perfil con un clic.
                            </p>
                        </div>

                        <div className="clientes-list-actions">
                            <div className="clientes-list-btn clientes-list-btn--ghost">
                                <UnificarModales />
                            </div>
                        </div>
                    </div>

                    <div className="clientes-list-toolbar">
                        <div className="clientes-list-filter-grid">
                            <div className="clientes-list-field">
                                <label className="clientes-list-label" htmlFor="clienteNombreFiltro">
                                    Buscar cliente
                                </label>
                                <input
                                    id="clienteNombreFiltro"
                                    type="text"
                                    className="clientes-list-input"
                                    placeholder="Escribe nombre del cliente"
                                    value={nombreCliente}
                                    onChange={(e) => setNombreCliente(e.target.value)}
                                />
                            </div>

                            <div className="clientes-list-field">
                                <label className="clientes-list-label" htmlFor="clienteEmpresaFiltro">
                                    Buscar empresa
                                </label>
                                <input
                                    id="clienteEmpresaFiltro"
                                    type="text"
                                    className="clientes-list-input"
                                    placeholder="Escribe nombre de empresa"
                                    value={nombreEmpresa}
                                    onChange={(e) => setNombreEmpresa(e.target.value)}
                                />
                            </div>

                            <div className="clientes-list-field">
                                <label className="clientes-list-label" htmlFor="clienteCorreoFiltro">
                                    Buscar correo
                                </label>
                                <input
                                    id="clienteCorreoFiltro"
                                    type="text"
                                    className="clientes-list-input"
                                    placeholder="Escribe correo"
                                    value={correoCliente}
                                    onChange={(e) => setCorreoCliente(e.target.value)}
                                />
                            </div>

                            <div className="clientes-list-field">
                                <label className="clientes-list-label" htmlFor="clienteTelefonoFiltro">
                                    Buscar telefono
                                </label>
                                <input
                                    id="clienteTelefonoFiltro"
                                    type="text"
                                    className="clientes-list-input"
                                    placeholder="Escribe telefono"
                                    value={telefonoCliente}
                                    onChange={(e) => setTelefonoCliente(e.target.value)}
                                />
                            </div>

                            <div className="clientes-list-field">
                                <label className="clientes-list-label">Tipo cliente</label>
                                <Select
                                    isMulti
                                    options={tipoClienteOptions}
                                    value={tipoCliente}
                                    onChange={(value) => setTipoCliente(value || [])}
                                    placeholder="Seleccionar tipo"
                                    styles={selectStyles}
                                />
                            </div>

                            <div className="clientes-list-field">
                                <label className="clientes-list-label">Ejecutivo</label>
                                <Select
                                    isMulti
                                    options={nameAdminOptions}
                                    value={nameAdmin}
                                    onChange={(value) => setNameAdmin(value || [])}
                                    placeholder="Seleccionar ejecutivo"
                                    styles={selectStyles}
                                />
                            </div>

                            <div className="clientes-list-field clientes-list-field--compact">
                                <label className="clientes-list-label" htmlFor="clientePageSize">
                                    Mostrar
                                </label>
                                <select
                                    id="clientePageSize"
                                    className="clientes-list-select"
                                    value={clientsPerPage}
                                    onChange={handleClientsPerPageChange}
                                >
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={30}>30</option>
                                    <option value={50}>50</option>
                                    <option value={70}>70</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="clientes-list-table-card">
                        <div className="clientes-list-table-scroll">
                            <table className="clientes-list-table">
                                <thead>
                                    <tr>
                                        <th>Cliente</th>
                                        <th>Empresa</th>
                                        <th>Correo</th>
                                        <th>Telefono</th>
                                        <th>Tipo cliente</th>
                                        <th>Credito</th>
                                        <th>Ejecutivo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentClients.length > 0 ? (
                                        currentClients.map((cliente) => (
                                            <tr key={cliente.id_cliente} className="clientes-list-row" onClick={() => handleRowClick(cliente)}>
                                                <td>
                                                    <span className="clientes-list-name">{truncarTexto(cliente.nombre_cliente || "SIN NOMBRE", 28)}</span>
                                                    <span className="clientes-list-subtext">ID {cliente.id_cliente}</span>
                                                </td>
                                                <td>{truncarTexto(cliente.nombre_empresa || "SIN EMPRESA", 28)}</td>
                                                <td>{truncarTexto(cliente.correo_cleinte || "SIN CORREO", 30)}</td>
                                                <td>{cliente.telefono_cliente || "SIN TELEFONO"}</td>
                                                <td>
                                                    <span className="clientes-list-chip">{cliente.tipo_cliente_cliente || "SIN TIPO"}</span>
                                                </td>
                                                <td>
                                                    <span
                                                        className={`clientes-list-chip ${cliente.credito_cliente && cliente.credito_cliente !== "No" ? "clientes-list-chip--credito" : ""}`}
                                                    >
                                                        {cliente.credito_cliente || "SIN MONTO DE CREDITO"}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="clientes-list-chip clientes-list-chip--success">{cliente.name_admin || "SIN ASIGNAR"}</span>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7">
                                                <div className="clientes-list-empty">No hay clientes para este filtro.</div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="clientes-list-pagination">
                        <button
                            type="button"
                            className="clientes-list-page-btn"
                            onClick={() => setCurrentPage((prev) => prev - 1)}
                            disabled={currentPage === 1}
                        >
                            Anterior
                        </button>
                        <div className="clientes-list-page-indicator">
                            Pagina <strong>{currentPage}</strong> de <strong>{totalPages}</strong>
                        </div>
                        <button
                            type="button"
                            className="clientes-list-page-btn"
                            onClick={() => setCurrentPage((prev) => prev + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Siguiente
                        </button>
                    </div>
                </section>
            </div>

            {openModal && selectedClient && <ModalCliente client={selectedClient} onClose={handleCloseModal} />}
        </>
    );
};
